import React from 'react';
import { Link } from 'react-router-dom';
import { HiOutlineArrowRight } from 'react-icons/hi';
import './About.css';

const values = [
  {
    emoji: '🎯',
    title: 'Quality First',
    desc: 'We obsess over output quality. Every pixel matters, every frame counts.',
  },
  {
    emoji: '⚡',
    title: 'Speed & Scale',
    desc: 'Built on GPU-accelerated infrastructure to process millions of files without breaking a sweat.',
  },
  {
    emoji: '🔒',
    title: 'Privacy by Design',
    desc: 'Your data is yours. We never train on user uploads and auto-delete files after processing.',
  },
  {
    emoji: '🤝',
    title: 'Developer Friendly',
    desc: 'REST APIs, SDKs, webhooks — we make it easy to integrate AI enhancement into any workflow.',
  },
];

const milestones = [
  { year: '2024', event: 'AIScaler founded with a mission to democratize AI media enhancement.' },
  { year: '2024', event: 'Core image upscaling engine developed with 2x, 4x, 8x capabilities.' },
  { year: '2025', event: 'Video enhancement and document processing modules launched.' },
  { year: '2025', event: 'REST API and developer platform released with Node.js & Python SDKs.' },
  { year: '2026', event: 'Batch processing, team collaboration, and enterprise features go live.' },
];

export default function About() {
  return (
    <div className="about-page">
      <section className="page-hero">
        <div className="container">
          <span className="section-label">About Us</span>
          <h1 className="section-title">Enhancing Media with AI</h1>
          <p className="section-subtitle">
            AIScaler is an AI-powered platform that makes it effortless to upscale and enhance images, videos, and documents to their highest potential.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="section">
        <div className="container about-mission">
          <div className="about-mission__content">
            <span className="section-label">Our Mission</span>
            <h2 className="section-title">Making High-Quality Media Accessible to Everyone</h2>
            <p className="section-subtitle">
              We believe every image, video, and document deserves to look its best. Whether you're an e-commerce store owner enhancing product photos, a content creator upscaling footage, or an archivist restoring historical documents — AIScaler gives you the tools to achieve professional-grade results without professional-grade effort.
            </p>
            <p className="about-mission__extra">
              Our platform combines state-of-the-art neural networks with intuitive design, so you can enhance media at scale with just a few clicks — or integrate AI enhancement directly into your apps via our developer platform.
            </p>
          </div>
          <div className="about-mission__visual">
            <div className="about-mission__card">
              <div className="about-mission__card-icon">🚀</div>
              <div className="about-mission__card-stat">Millions</div>
              <div className="about-mission__card-label">Files Enhanced</div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section about-values-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Our Values</span>
            <h2 className="section-title">What Drives Us</h2>
          </div>
          <div className="about-values-grid">
            {values.map(({ emoji, title, desc }) => (
              <div key={title} className="card about-value-card">
                <span className="about-value-card__emoji">{emoji}</span>
                <h3 className="about-value-card__title">{title}</h3>
                <p className="about-value-card__desc">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Our Journey</span>
            <h2 className="section-title">Milestones</h2>
          </div>
          <div className="about-timeline">
            {milestones.map(({ year, event }, i) => (
              <div key={i} className="about-timeline__item">
                <div className="about-timeline__dot" />
                <div className="about-timeline__year">{year}</div>
                <p className="about-timeline__event">{event}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="section-title">Want to Know More?</h2>
          <p className="section-subtitle" style={{ margin: '0 auto 32px' }}>
            Meet the team behind AIScaler or get in touch.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/team" className="btn btn-primary">Meet the Team <HiOutlineArrowRight /></Link>
            <Link to="/contact" className="btn btn-secondary">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
